import React, { useEffect, useId, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useT } from "@/lib/LanguageContext";
import useSiteContent from "@/lib/useSiteContent";
import { ChevronDown, Menu, X } from "lucide-react";
import SiteLink from "./SiteLink";

/**
 * Solunary's marketing header (solunary.health).
 *
 * ONE destination list, rendered twice: inline from `lg` up, and inside the hamburger
 * panel below it. Before BUG-202 there was no panel at all and the two clusters were gated
 * at two DIFFERENT breakpoints — `hidden md:flex` on the links, `hidden sm:flex` on the
 * login — so a visitor between 320px and 639px got no navigation AND no login, and one
 * between 640px and 767px got login but still no navigation. Links, login, language
 * switcher and hamburger now all switch at the same `lg`, which is the only arrangement in
 * which the mobile and desktop surfaces cannot disagree about what the site contains.
 *
 * Why `lg` and not `md`, the breakpoint the inline row used to carry. A width budget at
 * 768px, worst case FR, text-sm labels: 48px container padding + 48px bar gaps + 112px
 * logo (`md:h-28`) + ~367px for the four links and their gaps + ~336px for switcher, login
 * pill and trial CTA = ~911px. It does not fit, and it did not fit before either — with
 * six links and a 128px logo the same sum was ~1175px at 1024px, which is why the old
 * header ran off the side of every window narrower than about 1200px in French. Dropping
 * the two dead dropdowns takes ~190px out of the row and moving the switch-on to `lg`
 * leaves the 1024px band a positive (if thin) margin. Same reasoning, and the same
 * conclusion, as components/site/Navbar.jsx's `LG_INLINE_COUNT` comment.
 *
 * What is deliberately NOT here any more: the "Solutions" and "Resources" dropdowns. Both
 * rendered a ChevronDown with no handler and no panel (BUG-204), and nothing in the product
 * defines what they would contain. An arrow that opens nothing is worse than no arrow, so
 * the affordance is gone rather than filled with invented marketing pages. The header is a
 * flat list; what it links to is the operator's decision (Super Admin → Site → Links).
 *
 * Mobile budget (BUG-203): the logo was `h-32 w-32` — a flat 128px at every width below
 * `lg` — and neither it nor the trial CTA shrank, so logo + language switcher + CTA was
 * wider than a phone and clipped the CTA. The logo now steps up with the breakpoints, the
 * language switcher moves into the panel below `lg`, and the CTA keeps its place on the bar
 * at every width because it is the one control a phone visitor must be able to reach
 * without opening anything.
 */

/**
 * WHERE THE LINKS COME FROM. The destination list is Super Admin → Site → Links, location
 * `header` (contract §3.3) — the database is its only source, seeded once by migration
 * with the four links this file used to hard-code. An in-page anchor can only target one
 * of the registry's ANCHORS (`features`, `pricing`, `about`), which the server enforces on
 * write and the backend drift test pins against the ids this page renders.
 *
 * WHAT STAYS CHROME. The two login entry points and the trial CTA are auth/signup entry
 * points, not navigation: their destinations are fixed here, and only their labels are
 * editable site copy. `login-practitioner` and `login-solunary-team` are pinned testids —
 * the standing browser-QA scripts click them (scripts/browser_qa/railA_sa_approve.py).
 */
export default function Navbar() {
  // `t` only for the menu toggle's aria-label: accessibility text, not site copy.
  const t = useT("saas");
  const { text, image, links } = useSiteContent();
  const navigate = useNavigate();
  const headerLinks = links("header");
  const logo = image("image.brand.logoHeader", "/brand/solunary-logo.png");
  const loginLinks = [
    {
      key: "practitioner",
      href: "/admin/login",
      label: text("nav.practitionerLogin"),
      desc: text("landing.nav.practitionerLoginDesc"),
    },
    {
      key: "solunary-team",
      href: "/super-admin/login",
      label: text("nav.teamLogin"),
      desc: text("landing.nav.teamLoginDesc"),
    },
  ];

  const [menuOpen, setMenuOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const menuId = useId();
  const loginMenuId = useId();
  const menuToggleRef = useRef(null);
  const loginTriggerRef = useRef(null);
  const loginClusterRef = useRef(null);

  // Escape dismisses whichever disclosure is open and hands focus back to its trigger.
  // Without it the panel is a mouse-only affordance, which is not a usable menu.
  useEffect(() => {
    if (!menuOpen && !loginOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      if (menuOpen) {
        setMenuOpen(false);
        menuToggleRef.current?.focus();
      }
      if (loginOpen) {
        setLoginOpen(false);
        loginTriggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [menuOpen, loginOpen]);

  // A click-opened disclosure has to be closable by clicking away from it, or it outlives
  // the visitor's intent. Hover-close alone cannot do this: a click opener never gets a
  // mouseleave on a touch device.
  useEffect(() => {
    if (!loginOpen) return undefined;
    const onPointerDown = (e) => {
      if (loginClusterRef.current && !loginClusterRef.current.contains(e.target)) {
        setLoginOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [loginOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-sol-ivory/85 backdrop-blur-md border-b border-sol-midnight/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex items-center justify-between gap-3 lg:gap-6">
        <Link to="/platform" className="flex items-center flex-shrink-0" data-testid="platform-logo">
          <img
            src={logo.src}
            alt={logo.alt || "Solunary"}
            className="h-14 w-14 -my-2 sm:h-20 sm:w-20 sm:-my-4 md:h-28 md:w-28 md:-my-5 lg:h-36 lg:w-36 lg:-my-6 object-contain"
          />
        </Link>

        <div
          data-testid="nav-links"
          className="hidden lg:flex items-center gap-6 xl:gap-7 text-sm text-sol-midnight/70 font-medium whitespace-nowrap"
        >
          {headerLinks.map((link) => (
            <SiteLink
              key={link.id}
              link={link}
              testId={`nav-link-${link.id}`}
              className="hover:text-sol-midnight transition"
              onClick={closeMenu}
            />
          ))}
        </div>

        <div data-testid="nav-bar-actions" className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          {/* Below `lg` the switcher lives in the panel instead — see the mobile budget above. */}
          <div className="hidden lg:flex">
            <LanguageSwitcher variant="light" />
          </div>

          <div
            ref={loginClusterRef}
            data-testid="nav-login-cluster"
            className="hidden lg:flex relative"
            onMouseEnter={() => setLoginOpen(true)}
            onMouseLeave={() => setLoginOpen(false)}
          >
            <button
              type="button"
              ref={loginTriggerRef}
              data-testid="nav-login"
              aria-expanded={loginOpen}
              aria-controls={loginMenuId}
              onClick={() => setLoginOpen((v) => !v)}
              className="px-4 py-2 text-sm border border-sol-gold/60 rounded-xl hover:bg-sol-gold/10 transition text-sol-midnight font-medium flex items-center gap-1.5 whitespace-nowrap"
            >
              {text("nav.login")}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${loginOpen ? "rotate-180" : ""}`} />
            </button>
            {loginOpen && (
              // `top-full pt-2` rather than `top-full mt-2`: the visual gap is padding
              // INSIDE the absolutely positioned wrapper, so the pointer never crosses a
              // dead strip on its way from the trigger to the menu.
              <div id={loginMenuId} className="absolute right-0 top-full pt-2 w-64">
                <div
                  data-testid="nav-login-menu"
                  className="bg-white rounded-2xl border border-sol-midnight/10 shadow-xl overflow-hidden"
                >
                  {loginLinks.map((link) => (
                    <a
                      key={link.key}
                      href={link.href}
                      data-testid={`login-${link.key}`}
                      className="block px-5 py-3 hover:bg-sol-ivory border-b last:border-b-0 border-sol-midnight/5"
                    >
                      <div className="font-medium text-sol-midnight text-sm">{link.label}</div>
                      <div className="text-xs text-sol-midnight/55">{link.desc}</div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Button
            data-testid="cta-signup-nav"
            onClick={() => navigate("/platform/signup")}
            className="bg-sol-gold hover:bg-sol-gold/90 text-sol-midnight font-medium rounded-xl px-3.5 sm:px-5 py-2.5 text-sm"
          >
            {text("nav.startTrial")}
          </Button>

          <button
            type="button"
            ref={menuToggleRef}
            data-testid="nav-mobile-toggle"
            aria-expanded={menuOpen}
            aria-controls={menuId}
            aria-label={menuOpen ? t("nav.closeMenu") : t("nav.openMenu")}
            onClick={() => setMenuOpen((v) => !v)}
            className="lg:hidden p-2 rounded-xl border border-sol-midnight/15 text-sol-midnight hover:bg-sol-gold/10 transition"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div
          id={menuId}
          data-testid="nav-mobile-panel"
          className="lg:hidden border-t border-sol-midnight/5 bg-sol-ivory/95 backdrop-blur-md shadow-lg max-h-[80vh] overflow-y-auto"
        >
          <div className="px-4 py-4 flex flex-col">
            {headerLinks.map((link) => (
              <SiteLink
                key={link.id}
                link={link}
                testId={`nav-mobile-link-${link.id}`}
                className="py-2.5 text-base font-medium text-sol-midnight hover:text-sol-gold transition"
                onClick={closeMenu}
              />
            ))}

            <div className="mt-2 pt-3 border-t border-sol-midnight/10 flex flex-col">
              {loginLinks.map((link) => (
                <a
                  key={link.key}
                  href={link.href}
                  data-testid={`nav-mobile-login-${link.key}`}
                  onClick={closeMenu}
                  className="py-2.5"
                >
                  <div className="font-medium text-sol-midnight text-sm">{link.label}</div>
                  <div className="text-xs text-sol-midnight/55">{link.desc}</div>
                </a>
              ))}
            </div>

            <div className="mt-2 pt-3 border-t border-sol-midnight/10">
              <LanguageSwitcher variant="light" />
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
